import axios from "axios";
import { API_BASE_URL } from "../utils/constants";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];
let storeRef = null;

// Function to set store reference (called from App.js)
export const setStoreReference = (store) => {
  storeRef = store;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Log token info (first 20 chars for debugging)
      const tokenPreview = token.substring(0, 20) + "...";
      console.log("📤 [API REQUEST] Adding token to request:", {
        url: config.url,
        method: config.method,
        tokenPreview: tokenPreview,
      });
    } else {
      console.warn("⚠️ [API REQUEST] No token found in localStorage for request:", config.url);
    }
    return config;
  },
  (error) => {
    console.error("❌ [API REQUEST] Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Handle token expiration and refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    console.log("🔍 [API INTERCEPTOR] Response error:", {
      status: error.response?.status,
      url: originalRequest?.url,
      method: originalRequest?.method,
      isRetry: originalRequest?._retry,
    });

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log("🔄 [API INTERCEPTOR] 401 Unauthorized - Starting token refresh process");
      
      if (isRefreshing) {
        console.log("⏳ [API INTERCEPTOR] Token refresh already in progress, queueing request");
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            console.log("✅ [API INTERCEPTOR] Queued request resolved with new token");
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            console.error("❌ [API INTERCEPTOR] Queued request failed:", err);
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      console.log("🔄 [API INTERCEPTOR] Setting refresh flag to true");

      const refreshToken = localStorage.getItem("refreshToken");
      console.log("🔍 [API INTERCEPTOR] Refresh token from localStorage:", refreshToken ? "***present***" : "missing");

      if (!refreshToken) {
        console.error("❌ [API INTERCEPTOR] No refresh token found in localStorage");
        // No refresh token, logout user
        processQueue(error, null);
        isRefreshing = false;
        handleLogout();
        return Promise.reject(error);
      }

      try {
        console.log("🔄 [API INTERCEPTOR] Attempting to refresh token...");
        console.log("🔄 [API INTERCEPTOR] Refresh token endpoint:", `${API_BASE_URL}/api/auth/refresh-token`);
        console.log("🔍 [API INTERCEPTOR] Refresh token value:", refreshToken ? `${refreshToken.substring(0, 20)}...` : "MISSING");
        
        // Attempt to refresh the token - use plain axios to avoid interceptor loop
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh-token`,
          {
            refreshToken: refreshToken,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            // Don't use the api instance to avoid interceptor
          }
        );

        console.log("✅ [API INTERCEPTOR] Refresh token response received");
        console.log("🔍 [API INTERCEPTOR] Response data structure:", {
          hasData: !!response.data?.data,
          hasToken: !!response.data?.token,
          hasRefreshToken: !!response.data?.refreshToken,
          keys: Object.keys(response.data || {}),
        });

        // Handle both response structures: { data: { token, refreshToken } } or { token, refreshToken }
        const responseData = response.data?.data || response.data;
        const newToken = responseData?.token;
        const newRefreshToken = responseData?.refreshToken;

        console.log("🔍 [API INTERCEPTOR] Extracted tokens:", {
          hasNewToken: !!newToken,
          hasNewRefreshToken: !!newRefreshToken,
        });

        if (!newToken) {
          throw new Error("No token received from refresh endpoint");
        }

        // Update tokens in localStorage
        console.log("💾 [API INTERCEPTOR] Saving new tokens to localStorage");
        localStorage.setItem("authToken", newToken);
        
        // CRITICAL: Always update refreshToken if provided, otherwise keep the old one
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
          console.log("✅ [API INTERCEPTOR] New refresh token saved");
        } else {
          console.warn("⚠️ [API INTERCEPTOR] No new refresh token received, keeping old one");
          // Don't remove the old refresh token - keep it for next time
        }
        
        // Verify tokens were saved
        const verifyAuthToken = localStorage.getItem("authToken");
        const verifyRefreshToken = localStorage.getItem("refreshToken");
        console.log("✅ [API INTERCEPTOR] Token storage verification:", {
          authTokenSaved: !!verifyAuthToken,
          refreshTokenSaved: !!verifyRefreshToken,
          refreshTokenLength: verifyRefreshToken?.length || 0,
        });

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        console.log("✅ [API INTERCEPTOR] Updated original request with new token");

        // Process queued requests
        processQueue(null, newToken);
        isRefreshing = false;
        console.log("✅ [API INTERCEPTOR] Token refresh completed successfully");

        // Retry the original request
        console.log("🔄 [API INTERCEPTOR] Retrying original request:", originalRequest.url);
        return api(originalRequest);
      } catch (refreshError) {
        console.error("❌ [API INTERCEPTOR] Token refresh failed:", refreshError);
        console.error("❌ [API INTERCEPTOR] Refresh error details:", {
          message: refreshError.message,
          status: refreshError.response?.status,
          data: refreshError.response?.data,
        });
        
        // Refresh failed, logout user
        processQueue(refreshError, null);
        isRefreshing = false;
        handleLogout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Handle logout and redirect
const handleLogout = () => {
  // Clear localStorage
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");

  // Clear Redux state if store is available
  if (storeRef) {
    try {
      const { logout } = require("../redux/slices/authSlice");
      storeRef.dispatch(logout());
    } catch (err) {
      console.error("Error dispatching logout:", err);
    }
  }

  // Clear any pending requests
  failedQueue = [];
  isRefreshing = false;

  // Redirect to login page
  // Use window.location for hard redirect to ensure state is cleared
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

export default api;
